import Calc from "@/common/util/calculator"
import BaseFixmethod, { type FixmethodPosition } from "@/signomatic/app/fixmethods/base"
import CircleObject from "@/signomatic/app/objects/circle"
import GroupObject from "@/signomatic/app/objects/group"

export const DEFAULT_COUNT = 2
export const DEFAULT_DISTANCE_MM = 4
export const DEFAULT_SIZE_MM = 4
export const COUNT_MIN = 1
export const COUNT_MAX = 4
export const DIST_MIN_MM = 4
export const DIST_MAX_MM = 20
export const DIST_STEP_MM = 1
export const SIZE_MIN_MM = 3
export const SIZE_MAX_MM = 5
export const SIZE_STEP_MM = 0.5

export const defaultPatternByHoleCount: Record<number, string> = {
  1: "top_left",
  2: "center_x",
  4: "rect",
}

type Axis = "x" | "y" | "both"

export function patternValidationAxis(pattern: string): Axis {
  switch (pattern) {
    case "middle_left":
    case "middle_right":
    case "center_x":
    case "top":
    case "bottom":
      return "x"
    case "top_center":
    case "bottom_center":
    case "center_y":
    case "left":
    case "right":
      return "y"
    default:
      return "both"
  }
}

const clamp = (n: number, min: number, max: number) => Math.min(max, Math.max(min, n))
const roundToStep = (n: number, step: number) => Math.floor(n / step) * step

function getScrewSpec(material: SignState["material"], cappedScrew: boolean) {
  switch (material) {
    case "regular":
    case "aluminium":
    case "aluminium_lacquered":
      return { screwHoleDiameterMm: 4, headDiameter: cappedScrew ? 10 : 7 }
    case "stainless":
      return { screwHoleDiameterMm: 3.5, headDiameter: 5 }
    default:
      return { screwHoleDiameterMm: 4, headDiameter: 7 }
  }
}

function getRelevantSide(width: number, height: number, pattern: string) {
  const shortestSide = Math.min(width, height)

  if (patternValidationAxis(pattern) === "y") {
    return height
  } else if (patternValidationAxis(pattern) === "x") {
    return width
  } else {
    return shortestSide
  }
}

export function validateHoleSize(state: SignState, requested: number) {
  const {
    customHoleCount: customHoleCountState,
    customHoleDistanceMm: customHoleDistanceMmState,
    width,
    height,
  } = state

  const customHoleCount = customHoleCountState || DEFAULT_COUNT
  const customHoleDistanceMm = customHoleDistanceMmState || DEFAULT_DISTANCE_MM
  let holeSizeMm = roundToStep(clamp(requested, SIZE_MIN_MM, SIZE_MAX_MM), SIZE_STEP_MM)

  const relevantSide = getRelevantSide(
    width,
    height,
    state.customHoleControlPattern || defaultPatternByHoleCount[customHoleCount]
  )
  const diameter = holeSizeMm
  const radius = diameter / 2
  const clearance = 2 // min clearance between hole-edge and hole-hole
  const distance = customHoleDistanceMm

  // Check how many holes fit
  const fitsOneHole = distance - radius >= clearance && relevantSide - distance - radius >= clearance
  const fitsTwoHoles = fitsOneHole && relevantSide / 2 - distance - radius >= clearance / 2
  const fitsFourHoles =
    width / 2 - distance - radius >= clearance / 2 && height / 2 - distance - radius >= clearance / 2

  let limitExceeded = false
  let maxSizeMm = SIZE_MAX_MM

  // Calculate max hole size
  if (customHoleCount === 1) {
    limitExceeded = !fitsOneHole
    maxSizeMm = 2 * Math.min(distance - clearance, relevantSide - distance - clearance)
  } else if (customHoleCount === 2) {
    limitExceeded = !fitsTwoHoles
    maxSizeMm = 2 * Math.min(relevantSide / 2 - distance - clearance / 2, distance - clearance)
  } else if (customHoleCount === 4) {
    limitExceeded = !fitsFourHoles
    maxSizeMm = Calc.pxToMm(
      2 * Math.min(width / 2 - distance - clearance / 2, height / 2 - distance - clearance / 2, distance - clearance)
    )
  }

  maxSizeMm = Math.max(SIZE_MIN_MM, maxSizeMm)
  holeSizeMm = roundToStep(clamp(holeSizeMm, SIZE_MIN_MM, maxSizeMm), SIZE_STEP_MM)

  return {
    value: holeSizeMm,
    limitExceeded,
    max: Math.min(maxSizeMm, SIZE_MAX_MM),
  }
}

export function validateHoleDistance(state: SignState, requested: number) {
  const {
    width,
    height,
    fixmethod,
    customHoleCount: customHoleCountState,
    customHoleSizeMm: customHoleSizeMmState,
    material,
  } = state

  const customHoleCount = customHoleCountState || DEFAULT_COUNT
  const customHoleSizeMm = customHoleSizeMmState || DEFAULT_SIZE_MM
  const customHolesFixmethod = fixmethod === "custom_holes"
  const cappedScrew = fixmethod === "screw_capped"

  const { screwHoleDiameterMm, headDiameter } = getScrewSpec(material, cappedScrew)

  const relevantSide = getRelevantSide(
    width,
    height,
    state.customHoleControlPattern || defaultPatternByHoleCount[customHoleCount]
  )
  const diameterMm = customHolesFixmethod ? customHoleSizeMm : screwHoleDiameterMm
  const diameter = diameterMm
  const radius = diameter / 2
  const clearance = 2 // min clearance between hole-edge and hole-hole

  const minEdgeDistance = radius + clearance
  const minBetweenHoles = (customHolesFixmethod ? diameter : headDiameter) + clearance

  let max = 0

  // Calculate max distance
  if (customHoleCount === 1) {
    max = relevantSide - minEdgeDistance
  } else if (customHoleCount === 2) {
    max = (relevantSide - minBetweenHoles) / 2
  } else if (customHoleCount === 4) {
    max = Math.min((width - minBetweenHoles) / 2, (height - minBetweenHoles) / 2)
  }

  const minRaw = Math.max(minEdgeDistance, DIST_MIN_MM)
  const maxRaw = Math.min(max, DIST_MAX_MM)

  const min = roundToStep(minRaw, DIST_STEP_MM)
  max = roundToStep(maxRaw, DIST_STEP_MM)

  let validMm = requested
  validMm = roundToStep(clamp(validMm, min, max), DIST_STEP_MM)

  const limitExceeded = requested < min || requested > max

  return { value: validMm, limitExceeded, min, max }
}

export function validateHoleCount(state: SignState, requested: number, pattern?: string) {
  const {
    width,
    height,
    fixmethod,
    material,
    customHoleSizeMm: customHoleSizeMmState,
    customHoleDistanceMm: customHoleDistanceMmState,
  } = state

  const customHoleSizeMm = customHoleSizeMmState || DEFAULT_SIZE_MM
  const customHoleDistanceMm = customHoleDistanceMmState || DEFAULT_DISTANCE_MM
  const customHolesFixmethod = fixmethod === "custom_holes"
  const cappedScrew = fixmethod === "screw_capped"
  const { screwHoleDiameterMm } = getScrewSpec(material, cappedScrew)

  const relevantSide = getRelevantSide(
    width,
    height,
    pattern || state.customHoleControlPattern || defaultPatternByHoleCount[requested]
  )
  const diameter = customHolesFixmethod ? customHoleSizeMm : screwHoleDiameterMm
  const radius = diameter / 2
  const clearance = 2 // min clearance between hole-edge and hole-hole
  const distance = customHoleDistanceMm

  // Check how many holes fit
  const fitsOneHole = distance - radius >= clearance && relevantSide - distance - radius >= clearance
  const fitsTwoHoles = fitsOneHole && relevantSide / 2 - distance - radius >= clearance / 2
  const fitsFourHoles =
    width / 2 - distance - radius >= clearance / 2 && height / 2 - distance - radius >= clearance / 2

  let value = requested
  let limitExceeded = false
  let max = 4

  if (requested === 4 && !fitsFourHoles) {
    value = fitsTwoHoles ? 2 : 1
    limitExceeded = true
  } else if (requested === 2 && !fitsTwoHoles) {
    value = 1
    limitExceeded = true
  }

  if (fitsFourHoles) {
    max = 4
  } else if (fitsTwoHoles) {
    max = 2
  } else {
    max = 1
  }

  return { value, limitExceeded, max }
}

export default class CustomHolesFixmethod extends BaseFixmethod {
  positions(): FixmethodPosition[] {
    const {
      widthPx,
      heightPx,
      material,
      fixmethod,
      customHoleCount,
      customHoleDistanceMm,
      customHoleSizeMm,
      customHoleControlPattern,
    } = this.state
    const customHolesFixmethod = fixmethod === "custom_holes"
    const cappedScrew = fixmethod === "screw_capped"

    let screwHoleDiameter = 0
    let headDiameter = 0
    switch (material) {
      case "regular":
      case "aluminium":
      case "aluminium_lacquered":
        screwHoleDiameter = 4
        headDiameter = cappedScrew ? Calc.mmToPx(10) : Calc.mmToPx(7)
        break
      case "stainless":
        screwHoleDiameter = 3.5
        headDiameter = Calc.mmToPx(5)
        break
    }

    const holeCount = customHoleCount || DEFAULT_COUNT
    const controlPattern = customHoleControlPattern || defaultPatternByHoleCount[holeCount]
    let distance = Calc.mmToPx(customHoleDistanceMm || DEFAULT_DISTANCE_MM)
    let distanceAdjusted = Calc.mmToPx(
      (customHoleDistanceMm || DEFAULT_DISTANCE_MM) - (material === "stainless" ? 0.38 : 0.75)
    )
    const diameter = Calc.mmToPx(customHolesFixmethod ? customHoleSizeMm || DEFAULT_SIZE_MM : screwHoleDiameter)

    const positions: FixmethodPosition[] = []
    const defaults = { diameter, headDiameter }

    if (holeCount === 1) {
      distance = customHolesFixmethod ? distance : distanceAdjusted

      if (controlPattern === "top_left") {
        positions.push({ ...defaults, left: distance, top: distance })
        positions.push({ ...defaults, left: widthPx - distance, top: heightPx - distance, stretch: true })
      } else if (controlPattern === "top_right") {
        positions.push({ ...defaults, left: widthPx - distance, top: distance })
        positions.push({ ...defaults, left: distance, top: heightPx - distance, stretch: true })
      } else if (controlPattern === "top_center") {
        positions.push({ ...defaults, left: widthPx / 2, top: distance })
        positions.push({ ...defaults, left: widthPx / 2, top: heightPx - distance, stretch: true })
      } else if (controlPattern === "bottom_left") {
        positions.push({ ...defaults, left: distance, top: heightPx - distance })
        positions.push({ ...defaults, left: widthPx - distance, top: distance, stretch: true })
      } else if (controlPattern === "bottom_right") {
        positions.push({ ...defaults, left: widthPx - distance, top: heightPx - distance })
        positions.push({ ...defaults, left: distance, top: distance, stretch: true })
      } else if (controlPattern === "bottom_center") {
        positions.push({ ...defaults, left: widthPx / 2, top: heightPx - distance })
        positions.push({ ...defaults, left: widthPx / 2, top: distance, stretch: true })
      } else if (controlPattern === "middle_left") {
        positions.push({ ...defaults, left: distance, top: heightPx / 2 })
        positions.push({ ...defaults, left: widthPx - distance, top: heightPx / 2, stretch: true })
      } else if (controlPattern === "middle_right") {
        positions.push({ ...defaults, left: widthPx - distance, top: heightPx / 2 })
        positions.push({ ...defaults, left: distance, top: heightPx / 2, stretch: true })
      }
    } else if (holeCount === 2) {
      distanceAdjusted = customHolesFixmethod ? distance : distanceAdjusted

      if (controlPattern === "center_x") {
        positions.push({ ...defaults, left: distance, top: heightPx / 2 })
        positions.push({ ...defaults, left: widthPx - distance, top: heightPx / 2 })
      } else if (controlPattern === "top") {
        positions.push({ ...defaults, left: distance, top: distanceAdjusted })
        positions.push({ ...defaults, left: widthPx - distance, top: distanceAdjusted })
        positions.push({ ...defaults, left: distance, top: heightPx - distanceAdjusted, stretch: true })
        positions.push({ ...defaults, left: widthPx - distance, top: heightPx - distanceAdjusted, stretch: true })
      } else if (controlPattern === "bottom") {
        positions.push({ ...defaults, left: distance, top: heightPx - distanceAdjusted })
        positions.push({ ...defaults, left: widthPx - distance, top: heightPx - distanceAdjusted })
        positions.push({ ...defaults, left: distance, top: distanceAdjusted, stretch: true })
        positions.push({ ...defaults, left: widthPx - distance, top: distanceAdjusted, stretch: true })
      } else if (controlPattern === "left") {
        positions.push({ ...defaults, left: distanceAdjusted, top: distance })
        positions.push({ ...defaults, left: distanceAdjusted, top: heightPx - distance })
        positions.push({ ...defaults, left: widthPx - distanceAdjusted, top: distance, stretch: true })
        positions.push({ ...defaults, left: widthPx - distanceAdjusted, top: heightPx - distance, stretch: true })
      } else if (controlPattern === "right") {
        positions.push({ ...defaults, left: widthPx - distanceAdjusted, top: distance })
        positions.push({ ...defaults, left: widthPx - distanceAdjusted, top: heightPx - distance })
        positions.push({ ...defaults, left: distanceAdjusted, top: distance, stretch: true })
        positions.push({ ...defaults, left: distanceAdjusted, top: heightPx - distance, stretch: true })
      } else if (controlPattern === "center_y") {
        positions.push({ ...defaults, left: widthPx / 2, top: distance })
        positions.push({ ...defaults, left: widthPx / 2, top: heightPx - distance })
      }
    } else if (holeCount === 4) {
      if (controlPattern === "rotated_rect") {
        positions.push({ ...defaults, left: widthPx / 2, top: distance })
        positions.push({ ...defaults, left: distance, top: heightPx / 2 })
        positions.push({ ...defaults, left: widthPx / 2, top: heightPx - distance })
        positions.push({ ...defaults, left: widthPx - distance, top: heightPx / 2 })
      } else if (controlPattern === "rect") {
        positions.push({ ...defaults, left: distance, top: distance })
        positions.push({ ...defaults, left: distance, top: heightPx - distance })
        positions.push({ ...defaults, left: widthPx - distance, top: distance })
        positions.push({ ...defaults, left: widthPx - distance, top: heightPx - distance })
      }
    }

    return positions
  }

  private buildDrawable(holeStroke: string, holeFill: string, outerWidth: number) {
    const { widthPx, heightPx } = this.state
    const items: fabric.Object[] = []
    const positions = this.positions()

    if (positions.length === 1 && positions[0].left && positions[0].top && positions[0].diameter) {
      const holeRadius = positions[0].diameter / 2
      const left = positions[0].left
      const top = positions[0].top

      items.push(
        new CircleObject({
          radius: holeRadius,
          left,
          top,
          stroke: holeStroke,
          strokeWidth: outerWidth,
          startAngle: 0,
          endAngle: Calc.toDeg(Math.PI * 2),
          fill: holeFill,
        }).addClass("hang", "custom_holes")
      )

      // stretch out
      items.push(
        new CircleObject({
          radius: holeRadius,
          left: widthPx - left,
          top: heightPx - top,
          startAngle: 0,
          endAngle: Calc.toDeg(Math.PI * 2),
          stroke: "transparent",
          strokeWidth: outerWidth,
          fill: "transparent",
        })
      )
    } else {
      for (const e of positions) {
        const circleClass = e.stretch ? "stretch" : "hang"

        items.push(
          new CircleObject({
            left: e.left || 0,
            top: e.top || 0,
            radius: (e.diameter || 0) / 2,
            stroke: e.stretch ? "transparent" : holeStroke,
            strokeWidth: outerWidth,
            fill: e.stretch ? "transparent" : holeFill,
          }).addClass(circleClass, "custom_holes")
        )
      }
    }

    return [
      new GroupObject(items, {
        left: 0,
        top: 0,
        width: widthPx,
        height: heightPx,
      }),
    ]
  }

  topDrawable() {
    return this.buildDrawable("#aaa", "#F5F7F6", 1 / this.drawables.scale)
  }

  engravingDrawable() {
    return this.buildDrawable(this.drawables.engravingColor, "transparent", this.drawables.engravingStrokeWidth)
  }
}
